import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { sounds } from "@/lib/sounds";
import { logError } from "@/lib/logger";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | error
  const [tier, setTier] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("provider");
    const sid = provider === "paypal" ? params.get("subscription_id") : params.get("session_id");
    if (!sid) { setState("error"); return; }
    const statusUrl = provider === "paypal" ? `/payments/paypal/status/${sid}` : `/payments/status/${sid}`;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(statusUrl);
        if (data.payment_status === "paid") {
          setTier(data.tier);
          setState("paid");
          sounds.tack();
          await checkAuth();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (err) { logError("Payment status poll failed:", err); /* keep polling */ }
      tries.current += 1;
      if (tries.current > 12) { setState("error"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#5c4033] p-6">
      <div data-testid="payment-success" className="bg-[#fbf6ec] grain rounded-2xl p-8 max-w-sm w-full text-center paper-shadow-lift">
        {state === "checking" && (<><Loader2 className="w-12 h-12 mx-auto text-[#a0703c] animate-spin" /><p className="font-display font-bold text-lg mt-4 text-[#2b2620]">Confirming your payment…</p></>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#34c759]" />
          <h1 className="font-display font-black text-2xl mt-4 text-[#2b2620]">You're {tier === "family" ? "on Family" : "on Pro"}! 🎉</h1>
          <p className="text-sm text-[#6b6155] mt-2">Custom Board Studio, unlimited notes & polaroids, and all sound packs are unlocked.</p>
          <Button data-testid="success-to-board" onClick={() => navigate("/board")} className="w-full mt-6 rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold">Back to my board</Button>
        </>)}
        {state === "error" && (<>
          <XCircle className="w-14 h-14 mx-auto text-[#ff3b30]" />
          <h1 className="font-display font-black text-xl mt-4 text-[#2b2620]">We couldn't confirm that payment</h1>
          <p className="text-sm text-[#6b6155] mt-2">If you were charged, your plan will update shortly. Otherwise try again.</p>
          <Button onClick={() => navigate("/profile")} className="w-full mt-6 rounded-full bg-[#2b2620] text-[#fdf6e3] font-display font-bold">Back to profile</Button>
        </>)}
      </div>
    </div>
  );
}
